import React, { useCallback } from 'react';
import { PAPEvent } from '@dropbox/pap-events/base/event';
import { logPAPEvent } from 'js/sign-components/common/product-analytics-pipeline';
import { useHSUserOptional } from 'hellospa/common/hs-user';

/**
 * Minimal user data for PAP logging. Used when the component tree is not under
 * HSUserProvider. useLogPAPEvent uses HSUser
 * when present, otherwise falls back to this context.
 */
export type PAPUserData = {
  guid: string;
  dbxUserId?: string | null;
};

const papUserContext = React.createContext<PAPUserData | null>(null);

export function PAPUserProvider({
  value,
  children,
}: React.PropsWithChildren<{ value: PAPUserData | null }>) {
  return React.createElement(papUserContext.Provider, { value }, children);
}

/**
 * Returns a function that logs PAP events with the current user's userId and
 * foreignUserId attached automatically. Uses HSUser when present (e.g. inside
 * HelloSPA), otherwise falls back to PAPUserProvider context when set
 */
export function useLogPAPEvent(): (event: PAPEvent) => void {
  const hsUser = useHSUserOptional();
  const papUser = React.useContext(papUserContext);
  const user = hsUser ?? papUser;

  return useCallback(
    (event: PAPEvent) => {
      logPAPEvent(event, user?.dbxUserId ?? undefined, user?.guid);
    },
    [user?.guid, user?.dbxUserId],
  );
}
